import React from 'react';
import { X, Server, Wifi, Router, Package, Zap, HardDrive } from 'lucide-react';
import { Rack } from '../../types';

interface RackEquipmentSelectorProps {
  rack: Rack;
  onSelect: (equipmentId: string, port?: string) => void;
  onClose: () => void;
  isSelectingSource?: boolean;
}

const RackEquipmentSelector: React.FC<RackEquipmentSelectorProps> = ({ rack, onSelect, onClose, isSelectingSource = false }) => {
  const getIcon = (type: string) => {
    switch (type) {
      case 'ap':
        return <Wifi size={16} className="text-yellow-500" />;
      case 'switch':
        return <Router size={16} className="text-blue-500" />;
      case 'router':
        return <Router size={16} className="text-red-500" />;
      case 'server':
        return <Server size={16} className="text-purple-500" />;
      case 'patch-panel':
        return <Package size={16} className="text-orange-500" />;
      case 'poe-injector':
        return <Zap size={16} className="text-pink-500" />;
      case 'ups':
        return <HardDrive size={16} className="text-indigo-500" />;
      default:
        return <Package size={16} className="text-gray-500" />;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">
            {isSelectingSource ? 'Select Source Equipment' : 'Select Destination Equipment'}
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <X size={20} />
          </button>
        </div>

        {/* Equipment List */}
        <div className="p-4 max-h-96 overflow-y-auto">
          {rack.equipment.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <Server size={48} className="mx-auto mb-2 text-gray-300" />
              <p>No equipment installed in this rack</p>
            </div>
          ) : (
            <div className="space-y-2">
              {rack.equipment.map((rackEq) => (
                <button
                  key={rackEq.equipment.id}
                  onClick={() => onSelect(rackEq.equipment.id)}
                  className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    {getIcon(rackEq.equipment.type)}
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{rackEq.equipment.name}</div>
                      <div className="text-sm text-gray-600">
                        Unit {rackEq.startUnit + 1} • {rackEq.equipment.specifications?.ports || 0} ports
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default RackEquipmentSelector;
